import { array, bool, func, string } from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import AdvancedSearch from '../AdvancedSearch';
import TabbedFilters from './TabbedFilters';
import UntabbedFilters from './UntabbedFilters';
import { VALIDATION_MESSAGE } from '../../../consts/validationMessage';
import { shouldRestoreFilters } from '../../hooks';

const getDefaultValue = (filter) => {
    if (filter.valueIgnore) return filter.valueIgnore;
    if (filter.type === 'string') return '';
    if (filter.type === 'boolean') return false;
    if (filter.type === 'date') return null;
    if (filter.type === 'ejercicio') return '';
    if (filter.type === 'entityMultiple') return [];
    if (filter.type === 'selectLista') return (filter.emptyValue !== undefined) ? filter.emptyValue : (filter.options ? (filter.options?.[0]?.value ?? null) : 0);
    return 0;
};

const isDefaultValue = (value, filter) => {
    const defaultVal = getDefaultValue(filter);
    if (filter.type === 'entityMultiple') {
        return !value || (Array.isArray(value) && value.length === 0);
    }
    return value === defaultVal;
};

const mapInitialFormValues = (filters) =>
    filters.reduce((result, filter) => ({
        ...result,
        [filter.field]: {
            value: filter.presetValue ?? getDefaultValue(filter),
            label: filter.presetLabel ?? getDefaultValue(filter),
        },
    }), {});

// Day-precision para Date evita que un preset computado inline (ej. `new Date(Date.now() - 7d)`)
// genere una key distinta en cada re-render del parent y dispare la rehidratacion fuera de navegacion.
const serializePreset = (v) => {
    if (v == null) return '';
    if (v instanceof Date) return `D${v.getFullYear()}-${v.getMonth()}-${v.getDate()}`;
    return String(v);
};

const WrapperAdvancedSearch = forwardRef(({ filters, tabs, onSearch, requireFilter, onClear, onValidation, message }, ref) => {
    const location = useLocation();
    const navigationType = useNavigationType();
    
    const willRestoreFilters = shouldRestoreFilters(location, navigationType);
    
    const [selectedValues, setSelectedValues] = useState(() =>
        filters
            .filter(f => f.presetValue != null && f.presetValue !== getDefaultValue(f))
            .map(f => ({ field: f.field, value: f.presetValue, label: f.presetLabel ?? f.presetValue }))
    );
    const [formValues, formSet] = useState(mapInitialFormValues(filters));
    const [activeTabKey, setActiveTabKey] = useState(tabs?.[0]?.key || '');
    const [lastSearchTabKey, setLastSearchTabKey] = useState(tabs?.[0]?.key || '');
    const [requireMessage, setRequireMessage] = useState(null);
    const [initShowFilters, setInitShowFilters] = useState(!willRestoreFilters);

    const isInitialMount = useRef(true);
    const presetKey = filters.map(f => serializePreset(f.presetValue)).join('\0');

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        formSet(mapInitialFormValues(filters));
        setSelectedValues(
            filters
                .filter(f => f.presetValue != null && f.presetValue !== getDefaultValue(f))
                .map(f => ({ field: f.field, value: f.presetValue, label: f.presetLabel ?? f.presetValue }))
        );

        setTimeout(() => onSearch(), 1);
    }, [presetKey]);

    const handleInput = (filter, value, label) =>
        formSet({ ...formValues, [filter.field]: { value, label } });

    const filterMap = useMemo(() => filters.reduce((result, filter) => ({
        ...result,
        [filter.field]: filter
    }), {}), [filters]);

    useImperativeHandle(ref, () => ({
        activeTab: lastSearchTabKey,
        filters: Object.keys(filterMap).reduce((object, key) => {
            const value = selectedValues.find(x => x.field === key)?.value ?? getDefaultValue(filterMap[key]);
            return { ...object, [key]: typeof value === 'string' ? value.trim() : value };
        }, {}),
        formValues: formValues,
        selectedValues: selectedValues,
        restoreFilters: (savedFilters) => {
            if (!savedFilters || typeof savedFilters !== 'object') {
                console.warn('restoreFilters: savedFilters inválido', savedFilters);
                return;
            }
            
            if (savedFilters.activeTab && typeof savedFilters.activeTab === 'string') {
                setActiveTabKey(savedFilters.activeTab);
                setLastSearchTabKey(savedFilters.activeTab);
            }
            
            if (savedFilters.formValues && savedFilters.selectedValues) {
                const isValidFormValues = Object.values(savedFilters.formValues).every(
                    item => item && typeof item === 'object' && 'value' in item
                );
                
                if (isValidFormValues) {
                    formSet(savedFilters.formValues);
                    setSelectedValues(savedFilters.selectedValues);
                }
            } else if (savedFilters.filters) {
                const restoredFormValues = {};
                const restoredSelectedValues = [];
                
                Object.keys(savedFilters.filters).forEach((field) => {
                    const value = savedFilters.filters[field];
                    const filter = filterMap[field];
                    if (filter && !isDefaultValue(value, filter)) {
                        const labelStr = Array.isArray(value) ? `${value.length} seleccionados` : (value != null ? value.toString() : '');
                        restoredFormValues[field] = { value, label: labelStr };
                        restoredSelectedValues.push({ field, value, label: labelStr });
                    } else if (filter) {
                        const defaultVal = filter.presetValue ?? getDefaultValue(filter);
                        restoredFormValues[field] = {
                            value: defaultVal,
                            label: filter.presetLabel ?? (defaultVal != null ? defaultVal.toString() : '')
                        };
                    }
                });
                
                formSet(restoredFormValues);
                setSelectedValues(restoredSelectedValues);
            }
        }
    }), [selectedValues, formValues, filterMap, lastSearchTabKey]);

    const labels = useMemo(() => selectedValues.map(({ field, label }) => ({
        title: filterMap[field]?.title,
        field,
        valueIgnore: getDefaultValue(filterMap[field]),
        value: label
    })), [selectedValues, filterMap]);

    const onTabChange = (tab) => {
        setActiveTabKey(tab.key);

        const newFormValues = { ...formValues };

        tab.fields.forEach((field) => {
            const filter = filterMap[field];
            newFormValues[field] = {
                value: filter.presetValue ?? getDefaultValue(filter),
                label: filter.presetLabel ?? getDefaultValue(filter),
            };
        });

        formSet(newFormValues);
    };

    const commitFilters = () => {
        let hasActiveFilters = false;
        let values = [];

        Object.keys(formValues).forEach((field) => {
            let value = formValues[field].value;
            let label = formValues[field].label;
            if (typeof value === 'string') value = value.trim();
            if (typeof label === 'string') label = label.trim();
            if (!isDefaultValue(value, filterMap[field])) {
                hasActiveFilters = true;
                values.push({ field, value, label });
            }
        });

        if (tabs && tabs.length > 0) {
            const activeTab = tabs.find(tab => tab.key === activeTabKey);
            if (activeTab) {
                values = values.filter(v => activeTab.fields.includes(v.field));
            }
        }

        setSelectedValues(values);

        if (requireFilter && !hasActiveFilters) {
            setRequireMessage(VALIDATION_MESSAGE.REQUIRE_FILTER);
            onClear?.();
            return Promise.resolve(false);
        }

        setRequireMessage(null);
        setLastSearchTabKey(activeTabKey);
        return new Promise((resolve) => {
            setTimeout(() => resolve(onSearch(formValues)), 1);
        });
    };

    const buildAppliedValues = () => {
        let values = [];
        Object.keys(formValues).forEach((field) => {
            const value = formValues[field].value;
            if (!isDefaultValue(value, filterMap[field])) {
                values.push({ field, value, label: formValues[field].label });
            }
        });
        if (tabs && tabs.length > 0) {
            const activeTab = tabs.find(tab => tab.key === activeTabKey);
            if (activeTab) values = values.filter(v => activeTab.fields.includes(v.field));
        }
        return values;
    };

    const runValidation = () => (onValidation ? onValidation(buildAppliedValues()) : true);

    const onLabelRemoved = (values) => {
        const newFormValues = { ...formValues };

        Object.keys(values).forEach((key) => {
            const filter = filterMap[key];
            if (isDefaultValue(values[key], filter)) {
                const defaultVal = getDefaultValue(filter);
                const label = filter.type === 'entityMultiple' ? '' : defaultVal;
                newFormValues[key] = { value: defaultVal, label };
            }
        });

        formSet(newFormValues);
    };

    const hasTabs = tabs && tabs.length > 0;
    const hasPresetValues = filters.some(f => f.presetValue != null && f.presetValue !== getDefaultValue(f));

    const handleEnter = () => {
        if (runValidation()) commitFilters();
    };

    return (
        <AdvancedSearch
            labels={labels}
            onSearch={commitFilters}
            onValidation={runValidation}
            formValues={formValues}
            formSet={onLabelRemoved}
            initShowFilters={!hasPresetValues}
            message={requireMessage ?? message}
        >
            {hasTabs ? (
                <TabbedFilters
                    {...{ tabs, onTabChange, formValues, handleInput }}
                    filters={filterMap}
                    onEnter={handleEnter}
                    activeTabKey={activeTabKey}
                />
            ) : (
                <UntabbedFilters
                    {...{ filters, formValues, handleInput }}
                    onEnter={handleEnter}
                />
            )}
        </AdvancedSearch>
    );
});

WrapperAdvancedSearch.propTypes = {
    filters: array.isRequired,
    tabs: array,
    onSearch: func.isRequired,
    requireFilter: bool,
    onClear: func,
    message: string,
    onValidation: func,
};

export default WrapperAdvancedSearch;
